import {Button as AntButton, Modal, Select, Table, TableColumnsType} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../Button';
import config from '../../../../lib/config';
import Pagination from '../../../Pagination';
import useAxios from '../../../../hooks/useAxios';
import { IClient, IClientListParams, IGetClientList } from '../../../../models/financials.model';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import useNotification from '../../../../hooks/useNotification';
import { IResponse } from '../../../../models/common.api.model';
import { createClient, getClients, updateClient } from '../../../../api/financials.api';
import _ from 'lodash';

interface IProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    setClientInfo: (client: IClient) => void;
}

const ClientManageModal = ({visible, setVisible, setClientInfo}: IProps) => {

    const { t, i18n } = useTranslation();
    const { infoNotification, successNotification } = useNotification();
    const clientNameInput = useRef<HTMLInputElement>(null);

    const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
    const [searchClientName, setSearchClientName] = useState<string | undefined>();
    const [clientName, setClientName] = useState('');
    const [chargeName, setChargeName] = useState('');
    const [depositor, setDepositor] = useState('');
    const [telNo, setTelNo] = useState('');
    const [bankCode, setBankCode] = useState('');
    const [accountNo, setAccountNo] = useState('');
    const [clientsData, setClientData] = useState<IClient[]>([]);
    const [page, setPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState<any>(null);

    const { commonCodes } = useSelector((state: RootState) => state.commonCode);
    // SY0007 = 은행
    const SY0007 = commonCodes.find(item => item.commonCode === 'SY0007')?.detailCode;

    const {data: clientRes, isLoading, error, execute,} = useAxios<IGetClientList>();
    const {data: saveRes, isLoading: isLoading2, error: error2, execute: executeSave } = useAxios<IResponse<Number>>();

    useEffect(() => {
        initSearch();
    }, []);

    useEffect(() => {
        if (clientRes && !isLoading) {
            let tempClientList : IClient[]  = _.cloneDeep(clientRes.data);

            if (tempClientList.length > 0) {
                let tempNo = 1;
                tempClientList.forEach((item: IClient) => {
                    item.no = tempNo;
                    tempNo++;
                });
            }

            setClientData(tempClientList);
        }
    }, [clientRes, isLoading]);

    useEffect(() => {
        if(saveRes) {
            if(saveRes.result === "success") {
                successNotification(t('common.message.save1'));
                initSearch();
            }
        }
    }, [saveRes]);

    const initSearch = () => {
        //auth.hqYn === 'Y' ? currentCampus : auth.campus[0],
        const clientListParam: IClientListParams = {
            clientCampusCode: currentCampus,
        }

        execute(getClients(clientListParam));
    }

    const onSearch = () => {
        if (!clientRes) return;

        let tempClientList : IClient[] = _.cloneDeep(clientRes.data);      
        if(searchClientName) {
            tempClientList = tempClientList.filter((item: IClient) => item.clientName?.toLowerCase().includes(searchClientName.toLowerCase()));
        }
        let tempNo = 1;
        tempClientList.forEach((item: IClient) => {
            item.no = tempNo;
            tempNo++;
        });
        setClientData(tempClientList);
    };

    const clientRowClick = (record : IClient) => {
        setSelectedRow(record);
        
        setClientName(record.clientName);
        setChargeName(record.chargeName);
        setDepositor(record.depositor);
        setTelNo(record.telNo);
        setBankCode(record.bankCode);
        setAccountNo(record.accountNo);
    }

    const onNewRegist = () => {
        setSelectedRow(undefined);

        setClientName('');
        setChargeName('');
        setDepositor('');
        setTelNo('');
        setBankCode('');
        setAccountNo('');

        if (clientNameInput.current !== null) {
            clientNameInput.current.focus();
        }
    }

    const onSave = async () => {
        if(!clientName || !chargeName || !depositor || !telNo || !bankCode || !accountNo) {
            return infoNotification(t('payment.expense.msg4'));
        }

        //파라미터 셋팅(form)
        let isEdit = false;
        let clientCode = selectedRow?.clientCode;
        const form = new FormData();
        form.append('campusCode', currentCampus);   //auth.hqYn === 'Y' auth.campus[0]
        if(clientCode) {
            form.append('clientCode', clientCode);
            isEdit = true;
        }
        form.append('clientName', clientName);
        form.append('clientChargeName', chargeName);;
        form.append('clientDepositor', depositor);
        form.append('clientTelNo', telNo);
        form.append('clientBankCode', (bankCode) ? bankCode : '');
        form.append('clientAccNo', accountNo);

        if(isEdit) {
            executeSave(updateClient(clientCode, form));
        } else {
            executeSave(createClient(form));
        }

    }

    const columns: TableColumnsType<IClient> = [
        {
            title: '#',
            dataIndex: 'no',
            key: 'no',
            align: 'center',
            width: '50px',
            sorter: (a, b) => a.no - b.no
        },
        {
            title: t('campus.staff.placeholder2'),
            dataIndex: 'select',
            key: 'select',
            align: 'center',
            render: (value, record) => {
                return (<input type='radio' onChange={()=>{setSelectedRow(record)}} checked={selectedRow?.clientCode === record.clientCode}  name='select' />)
            } 
        },
        {
            title: t('payment.expense.label4'),
            dataIndex: 'clientName',
            key: 'clientName',
            align: 'center',
            sorter: (a, b) => a.clientName.localeCompare(b.clientName)
        },
        {
            title: t('payment.expense.column8'),
            dataIndex: 'chargeName',
            key: 'chargeName',
            align: 'center',
            sorter: (a, b) => a.chargeName.localeCompare(b.chargeName)
        },
        {
            title: t('homepage.member.20'),
            dataIndex: 'telNo',
            key: 'telNo',
            align: 'center',
            sorter: (a, b) => a.telNo.localeCompare(b.telNo)
        },
    ];

    return (
        <>
            {isLoading ? <Loader /> : null}
            <ErrorResponse error={error} />
            <Modal
                title={t('payment.expense.btn1')}
                width={750}
                visible={visible}
                onCancel={() => setVisible(false)}
                footer={[
                <div className='comm-btn__area justify-content-center'>
                    <AntButton
                        className='btn-basic pink'
                        onClick={onSave}
                    >
                    {t('common.button.save')}
                    </AntButton>
                    <AntButton key="cancel" className='btn-basic gray' onClick={() => setVisible(false)}>
                    {t('common.button.close')}
                    </AntButton>
                </div>
                ]}
            >
                <div>
                    <div className="comm-bx__between">
                        <h3 className='sub-title'>{t('payment.expense.column5')}</h3>
                        <div className="comm-btn__area right">
                        <input
                            type="text"
                            className="form-control w-240p"
                            placeholder={t('common.text.input')}
                            value={searchClientName}
                            onChange={(e) => setSearchClientName(e.target.value)}
                            onKeyPress={(e) => {
                            if (e.key === 'Enter') onSearch();
                            }}
                        />
                        <div className='comm-btn__area justify-content-center'>
                            <Button
                            className="btn-basic small black"
                            onClick={onSearch}
                            text={t('common.button.search')}
                            />
                        </div>
                        </div>
                    </div>
        
                    <div className='comm-table__area mt-0'>
                        <Table
                            columns={columns}
                            dataSource={clientsData || undefined}
                            pagination={{
                                current: page,
                                pageSize: config.smallPageSize,
                            }}
                            rowKey={'clientCode'}
                            onRow={(record) => {
                                return {
                                    onClick: () => {
                                        clientRowClick(record);
                                    },
                                };
                            }}
                            
                            rowClassName={(record: any) =>
                                record.clientCode === selectedRow?.clientCode
                                    ? 'chosen'
                                    : 'pointer'
                            }  
                        />
                    </div>
                    <div className="comm-bx__between">
                        <div></div>
                        <div className="common-paging center" style={{position:'absolute'}}>
                        <Pagination count={clientsData.length} page={page} setPage={setPage} perPage={config.smallPageSize} />
                        </div>
                        <div className='comm-btn__area right'>
                            <Button
                            className="btn-basic pink-line"
                            onClick={()=>{
                                if(selectedRow) {
                                    setClientInfo(selectedRow);
                                    setVisible(false);
                                } else {
                                    infoNotification(t('payment.expense.msg5'));
                                }
                            }}
                            text={t('payment.expense.btn2')}
                            />
                        </div>
                    </div>

                    <div>
                        <div className="comm-bx__between">
                            <h3 className='sub-title'>{t('payment.expense.title3')}</h3>
                            <div className='comm-btn__area right'>
                                <Button
                                    className="btn-basic pink"
                                    text={t('program.semester.button2')}
                                    onClick={onNewRegist}
                                />
                            </div>
                        </div>
                        <div className="comm-table__area rowType mt-0">
                            <table>
                                <colgroup>
                                    <col width="15%" />
                                    <col width="35%" />
                                    <col width="15%" />
                                    <col width="35%" />
                                </colgroup>
                                <tbody>
                                    <tr>
                                        <th>{t('payment.expense.label4')}</th>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control input-text"
                                                value={clientName}
                                                onChange={e => setClientName(e.target.value)}
                                                ref = {clientNameInput}
                                            />
                                        </td>
                                        <th>{t('payment.expense.column8')}</th>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control input-text"
                                                value={chargeName}
                                                onChange={e => setChargeName(e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>{t('payment.expense.column6')}</th>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control input-text"
                                                value={depositor}
                                                onChange={e => setDepositor(e.target.value)}
                                            />
                                        </td>
                                        <th>{t('homepage.member.20')}</th>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control input-text"
                                                value={telNo}
                                                onChange={e => setTelNo(e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>{t('campus.shuttle.column12')}</th>
                                        <td>
                                            <Select
                                                className="w-100 text-center"
                                                value={bankCode}
                                                onChange={(value)=>{
                                                    setBankCode(value);
                                                }}
                                                placeholder={t('statistics.regularTest.placeholder2')}
                                            >   
                                                {SY0007?.map((item) => {
                                                    return(
                                                        <Select.Option
                                                            key={item.detailCode}
                                                            value={item.detailCode}
                                                        >
                                                            {i18n.language === 'ko' ? item.korName : item.engName}
                                                        </Select.Option>
                                                    );
                                                })};
                                            </Select>
                                        </td>
                                        <th>{t('campus.shuttle.column13')}</th>
                                        <td>
                                            <div className="w-100">
                                                <input
                                                    type="text"
                                                    className="form-control input-text"
                                                    value={accountNo}
                                                    onChange={e => setAccountNo(e.target.value)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default ClientManageModal;